import { Dispatch, useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { getPaynowStatus, postGeneratePaynowQR } from '../../../api/financial/api';
import {
  FINANCIAL_KEY_GET_PAYNOW_STATUS,
  FINANCIAL_KEY_POST_GENERATE_PAYNOW_QR,
} from '../../../api/financial/key';
import { PostGeneratePaynowQRResponse } from '../../../api/financial/model';
import { useCustomMutate, useCustomQuery } from '../../../hooks/query';
import { ENetsPostMessageData } from '../components/ENetsPaymentInterimResult';
import {
  PaymentErrorAction,
  PaymentMethod,
  RESET_ERROR_ACTION,
  SET_ERROR_ACTION,
} from '../components/PaymentButton';
import { getENetsErrorMessages } from './errorMessageGenerator';
import { usePrevious } from '../../../hooks/usePrevious';
import { useCountDown } from '../../../hooks/timer';

const MESSAGE = 'message';
const GET_PAYNOW_STATUS_API_REFETCH_INTERVAL = 10000;

/**
 * Handle listening to postMessage data sent from ENetsInterimResult.tsx page
 *
 * @param selectedPaymentMethod
 * @param internalTransactionRef
 * @param paymentErrorDispatch
 * @param onSuccessPayment
 * @param setIsPaymentModalOpen
 */
export const useENetsWindowEventListener = (
  selectedPaymentMethod: PaymentMethod | null,
  internalTransactionRef: string,
  paymentErrorDispatch: Dispatch<PaymentErrorAction>,
  onSuccessPayment: (internalTransactionRef: string) => void,
  setIsPaymentModalOpen: (isOpen: boolean) => void
) => {
  const handleWindowEventListener = useCallback(
    (event: MessageEvent<ENetsPostMessageData>): void => {
      if (
        event.data &&
        event.data.ENetsPostMessage === 'ENetsPostMessage'
      ) {
        const { data } = event;
        if (data.isSuccess) {
          paymentErrorDispatch({ type: RESET_ERROR_ACTION });
          onSuccessPayment(internalTransactionRef);
          setIsPaymentModalOpen(false);
        } else {
          setIsPaymentModalOpen(false);

          const errorMessagesFromEnets = getENetsErrorMessages({
            isRetry: data.isRetry,
            netsStageRespCode: data.netsStageRespCode,
            netsTxnMsg: data.netsTxnMsg,
            merchantTxnRef: data.merchantTxnRef,
            netsTxnRef: data.netsTxnRef,
          });

          paymentErrorDispatch({
            type: SET_ERROR_ACTION,
            payload: {
              isErrorModalOpen: true,
              isRetry: data.isRetry,
              errorMessages: errorMessagesFromEnets,
            },
          });
        }
      }
    },
    [onSuccessPayment, internalTransactionRef, paymentErrorDispatch, setIsPaymentModalOpen]
  );

  useEffect(() => {
    if (selectedPaymentMethod === PaymentMethod.ENETS_CREDIT_CARD) {
      window.addEventListener(MESSAGE, handleWindowEventListener);
    }

    return () => {
      window.removeEventListener(MESSAGE, handleWindowEventListener);
    };
  }, [selectedPaymentMethod, handleWindowEventListener]);
};

export const usePayNowPolling = (
  selectedPaymentMethod: PaymentMethod | null,
  internalTransactionRef: string,
  enablePolling: boolean,
  modelOpen: boolean,
  setEnabled: (a: boolean) => void,
  onSuccessPayment: () => void
) => {
  // mutation to get QR code image
  const useQrCodeMutate = useCustomMutate<PostGeneratePaynowQRResponse, string>(
    FINANCIAL_KEY_POST_GENERATE_PAYNOW_QR,
    (csrf, variables) => postGeneratePaynowQR(csrf, variables).then((res) => res.data),
    {},
    true,
    false
  );
  const { mutate, data: qrCodeData } = useQrCodeMutate;

  const [isQrExpired, setIsQrExpired] = useState(false);
  const { countDownDisplay, isCountDownEnded } = usePayNowCountdownTimer(qrCodeData?.expiryDate);

  useEffect(() => {
    if (isCountDownEnded) {
      setIsQrExpired(true);
    }
  }, [isCountDownEnded]);

  const qrMutate = useCallback(
    (transactionRef: string) => {
      setIsQrExpired(false);
      mutate(transactionRef);
      setEnabled(true);
    },
    // Do not include mutate function in dependency, if not, will cause unnecessary mutation
    [mutate, setEnabled]
  );

  const previousModelOpen = usePrevious(modelOpen);

  useEffect(() => {
    if (!previousModelOpen && modelOpen) {
      setIsQrExpired(true);
    }
  }, [previousModelOpen, modelOpen]);

  // poll API to get status of paynow transaction
  const { refetch: refetchQrPaymentStatus } = useCustomQuery(
    [FINANCIAL_KEY_GET_PAYNOW_STATUS, internalTransactionRef],
    () => getPaynowStatus(internalTransactionRef).then((res) => res.data),
    {
      refetchInterval: GET_PAYNOW_STATUS_API_REFETCH_INTERVAL,
      cacheTime: 0,
      enabled: enablePolling && selectedPaymentMethod === PaymentMethod.PAYNOW_QR,
      onSuccess: (res) => {
        if (res.data === 'SUCCESS') {
          onSuccessPayment();
        }
      },
    }
  );

  useEffect(() => {
    if (selectedPaymentMethod === PaymentMethod.PAYNOW_QR && internalTransactionRef) {
      refetchQrPaymentStatus();
    }
  }, [refetchQrPaymentStatus, internalTransactionRef, selectedPaymentMethod]);

  const regenerateQrCode = useCallback(async () => {
    await refetchQrPaymentStatus();
    qrMutate(internalTransactionRef);
  }, [refetchQrPaymentStatus, internalTransactionRef, qrMutate]);

  return {
    useQrCodeMutate,
    regenerateQrCode,
    isQrExpired,
    countDownDisplay,
  };
};

const usePayNowCountdownTimer = (expiryDateOfQr?: string) => {
  const { reset, countDown, countDownEnded } = useCountDown(0);
  useEffect(() => {
    if (expiryDateOfQr) {
      const utcExpiryDateOfQr = dayjs(expiryDateOfQr).utc();
      const secondsToQrExpired = utcExpiryDateOfQr.diff(dayjs(), 'second');
      reset(secondsToQrExpired * 1000);
    }
  }, [expiryDateOfQr, reset]);

  return {
    countDownDisplay: countDown.format('mm:ss'),
    isCountDownEnded: countDownEnded,
  };
};
